// Applications: /pam/applications — the tenant's apps that sign users in
// through AuthNull (OpenID Connect, SAML) or ask it for the second factor (MFA
// API). POST /api/v1/idp/apps/list; the Assigned count reads each app's
// assignments once the list is in. MFA API apps have no assignments: theirs is
// the number of their app policies, from one ListPolicy call.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Table, Tooltip, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, TriangleAlert } from "lucide-react";

import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import { logOutUser } from "../../common";
import { countByApp, listAppPolicies } from "../../service/appPolicyService";
import { deleteApp, listAppAssignments, listApps, listLoginSources } from "../../service/idpService";
import { CopyButton, Note } from "../enduser/ssh-proxy/sshProxyUi";
import { AppLogo, Forbidden, PROTOCOL_LABEL, ProtocolBadge, appIdentifier, reportIdpError } from "./appsUi";

const TYPE_FILTERS = [
  { value: "oidc", label: "OIDC" },
  { value: "saml", label: "SAML" },
  { value: "mfa_api", label: "MFA API" },
];

export default function ApplicationsList() {
  const navigate = useNavigate();
  const [msg, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  // null until read; then whether an enabled sign-in source exists.
  const [hasSource, setHasSource] = useState(null);
  // appId → number of users + groups
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState("");
  const [type, setType] = useState(undefined);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listApps()
      .then((res) => {
        if (!alive) return;
        const list = res?.apps || [];
        setApps(list);
        // The Assigned column: one cheap call per app, filled in as they land.
        list
          .filter((a) => a.protocol !== "mfa_api")
          .forEach((a) =>
            listAppAssignments(a.id)
              .then((r) => alive && setCounts((c) => ({ ...c, [a.id]: (r?.assignments || []).length })))
              .catch(() => {}),
          );
        // MFA API apps: their policy count, grouped by policyJson.app.apps[0].
        const mfaApps = list.filter((a) => a.protocol === "mfa_api");
        if (mfaApps.length) {
          listAppPolicies()
            .then((policies) => {
              if (!alive) return;
              const byApp = countByApp(policies);
              setCounts((c) => ({ ...c, ...Object.fromEntries(mfaApps.map((a) => [a.id, byApp[a.id] || 0])) }));
            })
            .catch((f) => f?.logout && logOutUser());
        }
      })
      .catch((err) => alive && reportIdpError(err, { msg, fallback: "Could not read the applications.", setForbidden }))
      .finally(() => alive && setLoading(false));
    listLoginSources()
      .then((res) => alive && setHasSource((res?.loginSources || []).some((s) => s.enabled)))
      .catch(() => alive && setHasSource(null));
    return () => {
      alive = false;
    };
  }, [reloadKey, msg]);

  const confirmDelete = (app) =>
    modal.confirm({
      title: `Delete ${app.name}?`,
      content: "Users will no longer be able to sign in to this app through AuthNull.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteApp(app.id)
          .then(() => {
            msg.success(`${app.name} deleted.`);
            reload();
          })
          .catch((err) => reportIdpError(err, { msg, fallback: "Could not delete the application.", onNotFound: reload })),
    });

  const q = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      apps.filter(
        (a) =>
          (!type || a.protocol === type) &&
          (!q || a.name.toLowerCase().includes(q) || appIdentifier(a).toLowerCase().includes(q)),
      ),
    [apps, type, q],
  );

  // Sign-in apps need a sign-in source; an MFA API app does not.
  const needsSource = apps.some((a) => a.protocol === "oidc" || a.protocol === "saml");
  const showSourceBanner = !loading && needsSource && hasSource === false;

  const apiShown = visible.filter((a) => a.protocol === "mfa_api").length;
  const mixedAccess = apiShown > 0 && apiShown < visible.length;
  const accessHeader = apiShown === 0 ? "Assigned" : mixedAccess ? "Access" : "Policies";

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (name, a) => (
        <Link to={`/pam/applications/${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0, color: AN_COLORS.ink }}>
          <AppLogo app={a} size={26} />
          <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        </Link>
      ),
    },
    {
      title: "Type",
      dataIndex: "protocol",
      width: "7.5rem",
      render: (p) => <ProtocolBadge protocol={p} />,
    },
    {
      title: "Identifier",
      key: "identifier",
      render: (_, a) => {
        const v = appIdentifier(a);
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <MonoCell title={`${a.protocol === "saml" ? "Entity ID" : "Client ID"}: ${v}`}>{v}</MonoCell>
            </span>
            {v && <CopyButton text={v} />}
          </span>
        );
      },
    },
    {
      // MFA API apps are policy-only, so their number is a policy count. The
      // header names what is listed; with both kinds, each number says which.
      title: accessHeader,
      key: "assigned",
      width: mixedAccess ? "8.5rem" : "7rem",
      render: (_, a) =>
        counts[a.id] === undefined ? (
          <span style={{ color: AN_COLORS.muted }}>—</span>
        ) : a.protocol === "mfa_api" ? (
          <Tooltip title={counts[a.id] === 0 ? "No one can use this app yet." : "Policies that let users use this app"}>
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                color: counts[a.id] === 0 ? AN_COLORS.warn : undefined,
                fontWeight: counts[a.id] === 0 ? 600 : undefined,
              }}
            >
              {counts[a.id]}
              {mixedAccess ? (counts[a.id] === 1 ? " policy" : " policies") : ""}
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Users and groups who may sign in">
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {counts[a.id]}
              {mixedAccess ? " assigned" : ""}
            </span>
          </Tooltip>
        ),
    },
    {
      title: "Status",
      dataIndex: "enabled",
      width: "7.5rem",
      render: (on) => <StatusPill tone={on ? "success" : "neutral"} label={on ? "Enabled" : "Disabled"} />,
    },
    {
      title: "",
      key: "actions",
      width: "3.5rem",
      align: "right",
      render: (_, a) => (
        <RowActionMenu
          ariaLabel={`Actions for ${a.name}`}
          items={[
            { key: "edit", label: "Edit", onClick: () => navigate(`/pam/applications/${a.id}`) },
            { type: "divider" },
            { key: "delete", label: "Delete…", danger: true, onClick: () => confirmDelete(a) },
          ]}
        />
      ),
    },
  ];

  const applied = [
    q && { key: "search", label: `Search: ${search.trim()}`, clear: () => setSearch("") },
    type && { key: "type", label: `Type: ${PROTOCOL_LABEL[type]}`, clear: () => setType(undefined) },
  ].filter(Boolean);

  const addButton = (
    <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={() => navigate("/pam/applications/new")}>
      Add application
    </Button>
  );

  const empty =
    !loading && apps.length === 0 ? (
      <div style={{ padding: "28px 12px", display: "grid", gap: 12, justifyItems: "center" }}>
        <span style={{ fontSize: 13, color: AN_COLORS.muted, maxWidth: "30rem" }}>
          No applications yet. Add the applications your users should sign in to through AuthNull.
        </span>
        {addButton}
      </div>
    ) : (
      tableEmpty(loading, "No applications match your filters.").emptyText
    );

  return (
    <PageShell>
      {msgCtx}
      {modalCtx}
      <PageHeader
        eyebrow="Applications"
        title="Applications"
        subtitle="Apps your users sign in to through AuthNull, with their directory account and a push or a code."
        chips={!forbidden && <CountChip count={apps.length} label={apps.length === 1 ? "Application" : "Applications"} />}
        actions={!forbidden && addButton}
      />

      {forbidden ? (
        <Forbidden />
      ) : (
        <>
          {showSourceBanner && (
            <div style={{ marginBottom: 14 }}>
              <Note tone="warn" icon={<TriangleAlert size={15} />}>
                Users can't sign in to applications yet: choose the directory they sign in with.{" "}
                <Link to="/pam/applications/signInSource" style={{ fontWeight: 600, color: "inherit", textDecoration: "underline" }}>
                  Sign-in source
                </Link>
              </Note>
            </div>
          )}

          <FilterCard
            columns="minmax(0, 1.6fr) minmax(0, 1fr)"
            applied={applied}
            onClearAll={() => {
              setSearch("");
              setType(undefined);
            }}
            showing={visible.length}
            total={apps.length}
          >
            <FilterField label="Search">
              <Input
                allowClear
                prefix={<Search size={14} color={AN_COLORS.muted} />}
                placeholder="Name, client ID or entity ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ height: 36 }}
              />
            </FilterField>
            <FilterField label="Type">
              <Select allowClear placeholder="Any" value={type} onChange={setType} options={TYPE_FILTERS} style={{ width: "100%", height: 36 }} />
            </FilterField>
          </FilterCard>

          <TableCard>
            <Table
              {...anTableProps}
              columns={columns}
              dataSource={visible}
              rowKey="id"
              loading={loading}
              tableLayout="fixed"
              scroll={{ x: "50rem" }}
              locale={{ emptyText: empty }}
              pagination={visible.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            />
          </TableCard>
        </>
      )}
    </PageShell>
  );
}
